import { useCallback, useEffect, useMemo, useState } from 'react'
import { unstable_useBlocker, useParams } from 'react-router-dom'

import { Dialog } from '@factorialco/f0-react/dist/experimental'
import {
  FindWorkflowDocument,
  LoadFormDocument,
  ProcurementTypePublishChangesDocument,
  ProcurementTypePublishDocument,
  RequestTypeDetailDocument,
  ProcurementTypeVersionsDocument,
  SaveProcurementTypeDocument,
} from 'generated/resources/graphql'
import { isEqual } from 'lodash'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { Result, UnhandledResult, handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  FromFragmentToQuestionnaire,
  FromQuestionaireToMutationVariables,
} from 'modules/forms/components/newFormEditor/helpers'
import { QuestionnaireType } from 'modules/forms/components/newFormEditor/types'
import { formFromData } from 'modules/forms/hooks/useForms'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { RequestTypeDetailHeader } from 'modules/procurement/components/RequestTypeDetailHeader'
import PublishTypeModal from 'modules/procurement/components/procurementTypes/PublishTypeModal'
import SaveRequestTypeModal from 'modules/procurement/components/procurementTypes/SaveRequestTypeModal'
import {
  VersionHistory,
  type RestoredVersionData,
} from 'modules/procurement/components/procurementTypes/VersionHistory'
import TabContent from 'modules/procurement/pages/RequestTypeDetailPage/TabContent'
import { WorkflowProvider, useWorkflow } from 'modules/workflows/contexts/WorkflowProvider'
import { getValidationErrorSummary } from 'modules/workflows/lib/workflowValidation'

import { flattenElements } from './helpers/flattenElements'
import { getFeatureFlags } from './helpers/getFeatureFlags'
import { getWorkflow } from './helpers/getWorkflow'
import {
  buildElementLabelMap,
  getAffectedVariableNames,
  validateFormWorkflowConsistency,
} from './helpers/validateFormWorkflowConsistency'

const defaultFormState: QuestionnaireType = {
  title: '',
  description: '',
  elements: [],
}

type ModalType = 'publish' | 'save' | null

function RequestTypeDetailPageContent() {
  const { id } = useParams<{ id: string }>()
  const { employee, company } = useCurrentSession()
  const [activeModal, setActiveModal] = useState<ModalType>(null)
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false)
  const [restoredVersion, setRestoredVersion] = useState<RestoredVersionData | null>(null)

  const refetchQueries = [
    RequestTypeDetailDocument,
    FindWorkflowDocument,
    LoadFormDocument,
    ProcurementTypeVersionsDocument,
  ]

  /* Mutations */
  const [saveProcurementType, { loading: isSaving }] = useMutation(SaveProcurementTypeDocument, {
    extractorFn: (data) => data.procurementMutations.saveProcurementType,
    refetchQueries,
  })

  const [publishProcurementType, { loading: isPublishing }] = useMutation(
    ProcurementTypePublishDocument,
    {
      extractorFn: (data) => data.procurementMutations.publishProcurementType,
      refetchQueries,
    }
  )

  const [publishChangesProcurementType, { loading: isPublishingChanges }] = useMutation(
    ProcurementTypePublishChangesDocument,
    {
      extractorFn: (data) => data.procurementMutations.publishChangesProcurementType,
      refetchQueries,
    }
  )

  /* Queries */

  // Get the data for the entire RequestType
  const {
    data: requestTypeDetailData,
    loading,
    error,
  } = useQuery(RequestTypeDetailDocument, {
    variables: {
      requestTypeId: id ?? '',
      companyId: company.id,
    },
    skip: !id || !company?.id,
  })

  // Feature flags check
  const { isProcurementV2FeatureEnabled, isCompleteFormEnabled, isVendorOnboardingEnabled } =
    getFeatureFlags(requestTypeDetailData)

  // Define the request type to be used in the page and dependencies
  const requestType = requestTypeDetailData?.procurement?.typesConnection?.edges?.[0]?.node
  const isTypePublished = requestType?.enabled ?? false

  // Query type versions to detect the current user's draft
  const { data: versionsData, error: versionsError } = useQuery(ProcurementTypeVersionsDocument, {
    variables: { procurementTypeId: id ?? '' },
    skip: !id,
  })

  const userDraft = useMemo(() => {
    if (!versionsData || versionsError) return null
    const nodes = versionsData.procurement?.typeVersionsConnection?.edges?.map((e) => e.node) ?? []
    return nodes.find((v) => v.status === 'draft' && v.createdBy.id === employee.id) ?? null
  }, [versionsData, versionsError, employee.id])

  // When the user has a draft, load the draft's form/workflow instead of the live ones.
  // A restored version takes highest priority (user picked a version from history).
  const liveFormId = requestType?.formsConnection?.edges?.[0]?.node?.id
  const liveWorkflowId = requestType?.workflowsConnection?.edges?.[0]?.node?.id
  const versionsReady = versionsData !== undefined
  const effectiveFormId =
    restoredVersion?.formId ?? (versionsReady ? (userDraft?.formId ?? liveFormId) : undefined)
  const effectiveWorkflowId =
    restoredVersion?.workflowId ??
    (versionsReady ? (userDraft?.workflowId ?? liveWorkflowId) : undefined)

  const publishedVersionNumber = useMemo(() => {
    if (!versionsData) return undefined
    const activeVersion = versionsData.procurement?.typeVersionsConnection?.edges
      ?.map((e) => e.node)
      ?.find((v) => v.status === 'active')
    return activeVersion?.versionNumber
  }, [versionsData])

  const currentEditingVersionId = useMemo(() => {
    if (restoredVersion) return restoredVersion.versionId
    if (userDraft) return userDraft.id
    return versionsData?.procurement?.typeVersionsConnection?.edges
      ?.map((e) => e.node)
      ?.find((v) => v.status === 'active')?.id
  }, [restoredVersion, userDraft, versionsData])

  const draftVersionInfo = useMemo(() => {
    if (restoredVersion) {
      return {
        versionNumber: restoredVersion.versionNumber,
        authorFirstName: restoredVersion.authorFirstName,
        authorLastName: restoredVersion.authorLastName,
      }
    }
    if (!userDraft) return undefined
    return {
      versionNumber: userDraft.versionNumber,
      authorFirstName: userDraft.createdBy.firstName,
      authorLastName: userDraft.createdBy.lastName,
    }
  }, [restoredVersion, userDraft])

  // Get the workflow data for the request type
  const { data: workflowsData } = useQuery(FindWorkflowDocument, {
    variables: {
      companyId: company.id,
      workflowId: effectiveWorkflowId ?? '',
    },
    skip: !isProcurementV2FeatureEnabled || !company?.id || !effectiveWorkflowId,
  })

  const { navigation } = useNavigation()

  /* Data hooks (form and workflow) */
  // Workflow data
  const workflowState = useWorkflow()
  const {
    isDirty,
    getSerializedNodes,
    getSerializedEdges,
    getNodesAndEdges,
    resetDirty: resetWorkflowDirty,
    validateWorkflow: validateCurrentWorkflow,
  } = workflowState
  const { workflow, hasWorkflowChanges, isLastWorkflowVersionPublished } = getWorkflow(
    workflowsData,
    isDirty
  )

  // Form data
  const { data: effectiveFormData, loading: effectiveFormLoading } = useQuery(LoadFormDocument, {
    variables: {
      id: String(effectiveFormId ?? ''),
      resourceType: 'Procurement::Type',
    },
    skip: !effectiveFormId,
  })

  // Form editor data
  const [formState, setFormState] = useState<QuestionnaireType>(defaultFormState)
  const [savedFormState, setSavedFormState] = useState<QuestionnaireType>(defaultFormState)

  const form = effectiveFormData?.forms?.form ?? null
  const formId = effectiveFormId ?? form?.id

  const formModel = useMemo(() => {
    if (!form) return undefined
    return formFromData({
      ...form,
      resourceId: form.resourceId ?? undefined,
      description: form.description ?? '',
      authorFullName: form.author?.employee?.fullName ?? '',
      authorAvatarUrl: form.author?.employee?.avatar?.url ?? '',
      elements: flattenElements(formState.elements, form.id).map((el) => ({
        ...el,
        description: el.description ?? '',
      })),
    })
  }, [form, formState])

  const initialFormState = useMemo(
    () => (form ? FromFragmentToQuestionnaire(form) : defaultFormState),
    [form]
  )

  useEffect(() => {
    setSavedFormState(initialFormState)
    setFormState(initialFormState)
  }, [initialFormState])

  const isFormDirty = useMemo(
    () => !isEqual(formState, savedFormState),
    [formState, savedFormState]
  )

  const hasFormChanges = isFormDirty

  const lastRemoteFormRaw = requestType?.formsConnection?.edges?.[0]?.node
  const isLastFormVersionPublished = lastRemoteFormRaw?.status === 'active'

  const discardWorkflowChanges = useCallback(() => {
    resetWorkflowDirty()
  }, [resetWorkflowDirty])

  const hasRestoredContent = restoredVersion !== null && !effectiveFormLoading
  const hasLocalChanges = hasWorkflowChanges || hasFormChanges || hasRestoredContent
  const hasDraftChanges =
    !isLastFormVersionPublished || !isLastWorkflowVersionPublished || hasRestoredContent

  /* Navigation blocking for unsaved changes (form + workflow) */
  const [navConfirmModalOpen, setNavConfirmModalOpen] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null)

  // Block browser close/refresh when there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasLocalChanges) {
        event.preventDefault()
        event.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasLocalChanges])

  // Block in-app navigation when there are unsaved changes.
  const shouldBlockNav = useCallback(
    ({
      currentLocation,
      nextLocation,
    }: {
      currentLocation: { pathname: string; search: string }
      nextLocation: { pathname: string; search: string }
    }) => {
      if (!hasLocalChanges) return false
      if (
        currentLocation.pathname === nextLocation.pathname &&
        currentLocation.search === nextLocation.search
      )
        return false
      return true
    },
    [hasLocalChanges]
  )
  const navBlocker = unstable_useBlocker(shouldBlockNav)

  // Show confirm modal when navigation is blocked
  useEffect(() => {
    if (navBlocker.state === 'blocked') {
      setNavConfirmModalOpen(true)
      setPendingNavigation(() => () => navBlocker.proceed())
    }
  }, [navBlocker])

  const handleNavContinue = useCallback(() => {
    setNavConfirmModalOpen(false)
    setPendingNavigation(null)
    if (navBlocker.state === 'blocked') {
      navBlocker.reset()
    }
  }, [navBlocker])

  // Form editor handlers
  const handleFormDiscard = useCallback(() => {
    setFormState(savedFormState)
  }, [savedFormState])

  const handleNavDiscard = useCallback(() => {
    if (isFormDirty) {
      handleFormDiscard()
    }
    if (hasWorkflowChanges) {
      discardWorkflowChanges()
    }
    setRestoredVersion(null)
    setNavConfirmModalOpen(false)
    pendingNavigation?.()
    setPendingNavigation(null)
  }, [
    isFormDirty,
    hasWorkflowChanges,
    handleFormDiscard,
    discardWorkflowChanges,
    pendingNavigation,
  ])

  const handleNavCancel = useCallback(() => {
    setNavConfirmModalOpen(false)
    setPendingNavigation(null)
    if (navBlocker.state === 'blocked') {
      navBlocker.reset()
    }
  }, [navBlocker])

  // Helper to navigate to workflow tab
  const navigateToWorkflowTab = () => {
    const workflowPath = navigation.settings.procurement.requestType.id.fullPath({
      id: id || '',
    })
    window.history.pushState({}, '', `${workflowPath}?tab=workflows`)
    window.dispatchEvent(new PopStateEvent('popstate'))
  }

  /* Handlers */

  const handleSave = async () => {
    if (!id) {
      setActiveModal(null)
      app.setFlash({ type: 'wrong', message: i18n.t('procurement.types.save.fail') })
      return
    }

    // Validate workflow before saving if there are workflow changes
    if (workflow && (hasWorkflowChanges || hasRestoredContent)) {
      const validationResult = validateCurrentWorkflow()

      if (!validationResult.isValid) {
        // Close the save modal
        setActiveModal(null)

        // Navigate to workflow tab
        navigateToWorkflowTab()

        // Show validation error message
        const errorSummary = getValidationErrorSummary(validationResult.errors)
        app.setFlash({
          type: 'wrong',
          message: errorSummary || i18n.t('procurement.types.save.workflow_validation_failed'),
          time: 5000,
        })
        return
      }
    }

    // Validate that form changes don't break workflow conditions
    if (formModel && isFormDirty && workflow) {
      const { nodes } = getNodesAndEdges()
      const consistency = validateFormWorkflowConsistency(formModel, nodes, workflow)

      if (!consistency.isValid) {
        setActiveModal(null)
        const labelMap = buildElementLabelMap(formModel)
        app.setFlash({
          type: 'wrong',
          message: i18n.t('procurement.types.save.form_workflow_consistency_error', {
            variables: getAffectedVariableNames(consistency.issues, labelMap),
          }),
          time: 5000,
        })
        return
      }
    }

    const shouldSaveForm = hasFormChanges
    const shouldSaveWorkflow = hasWorkflowChanges

    const currentFormData =
      formId && shouldSaveForm && !effectiveFormLoading && form != null
        ? {
            id: formId,
            elements:
              FromQuestionaireToMutationVariables(formId, form.anonymous ?? false, formState)
                .elements ?? [],
          }
        : undefined

    const workflowData =
      workflow && shouldSaveWorkflow
        ? { id: workflow.id, edges: getSerializedEdges(), nodes: getSerializedNodes() }
        : undefined

    const basedOnVersionId = restoredVersion?.versionId

    const action = await saveProcurementType({
      variables: {
        id: id,
        authorId: employee.id,
        companyId: company.id,
        form: currentFormData,
        workflow: workflowData,
        basedOnVersionId,
      },
    })

    const result = handleResult(action, () => {
      setActiveModal(null)
      app.setFlash({ type: 'wrong', message: i18n.t('procurement.types.save.fail') })
    })

    if (result?.success) {
      if (isFormDirty) {
        setSavedFormState(formState)
      }
      if (hasWorkflowChanges) {
        resetWorkflowDirty()
      }
      setRestoredVersion(null)
      setActiveModal(null)
      app.setFlash({ type: 'correct', message: i18n.t('procurement.types.save.success') })
    }
  }

  const handleVersionRestore = useCallback((data: RestoredVersionData) => {
    setRestoredVersion(data)
  }, [])

  const handlePublish = async () => {
    const isPublishChangesFlow = isTypePublished && workflow?.id

    const publishFormId = formId

    if (!id || !publishFormId) {
      setActiveModal(null)
      app.setFlash({
        type: 'wrong',
        message: isPublishChangesFlow
          ? i18n.t('procurement.types.publish_changes.fail')
          : i18n.t('procurement.types.publish.fail'),
      })
      return
    }

    // Validate workflow before publishing if there is a workflow
    if (workflow) {
      const validationResult = validateCurrentWorkflow()

      if (!validationResult.isValid) {
        // Close the publish modal
        setActiveModal(null)

        // Navigate to workflow tab
        navigateToWorkflowTab()

        // Show validation error message
        const errorSummary = getValidationErrorSummary(validationResult.errors)
        app.setFlash({
          type: 'wrong',
          message: errorSummary || i18n.t('procurement.types.publish.workflow_validation_failed'),
          time: 5000,
        })
        return
      }

      // Validate that form changes don't break workflow conditions
      if (formModel) {
        const { nodes } = getNodesAndEdges()
        const consistency = validateFormWorkflowConsistency(formModel, nodes, workflow)

        if (!consistency.isValid) {
          setActiveModal(null)
          const labelMap = buildElementLabelMap(formModel)
          app.setFlash({
            type: 'wrong',
            message: i18n.t('procurement.types.save.form_workflow_consistency_error', {
              variables: getAffectedVariableNames(consistency.issues, labelMap),
            }),
            time: 5000,
          })
          return
        }
      }
    }

    const typeId = id

    let action: UnhandledResult<Result<unknown>>

    // If type is already published and workflow exists, publish changes
    if (isPublishChangesFlow) {
      action = await publishChangesProcurementType({
        variables: {
          companyId: company.id,
          procurementTypeId: typeId,
          formId: publishFormId,
          workflowId: workflow.id,
        },
      })
    } else {
      // Otherwise, do initial publish (creates workflow if needed)
      action = await publishProcurementType({
        variables: {
          companyId: company.id,
          procurementTypeId: typeId,
          formId: publishFormId,
          workflowId: workflow?.id,
        },
      })
    }

    const result = handleResult(action, (errors) => {
      // Close modal on error
      setActiveModal(null)

      // Show structured errors (like workflow validation errors) as flash messages
      if (Object.keys(errors).length) {
        const errorMessages = Object.values(errors).join(', ')
        app.setFlash({
          type: 'wrong',
          message: errorMessages,
        })
      }
    })

    if (result.success) {
      setActiveModal(null)
      app.setFlash({
        type: 'correct',
        message: isPublishChangesFlow
          ? i18n.t('procurement.types.publish_changes.success')
          : i18n.t('procurement.types.publish.success'),
      })
    }
  }

  return (
    <ResponsivePage.Body
      authorized={isProcurementV2FeatureEnabled}
      error={error || versionsError}
      loading={loading}
      showHeader={false}
      variant="fullscreen"
    >
      {/* Unsaved changes confirmation dialog (covers form + workflow) */}
      <Dialog
        open={navConfirmModalOpen}
        onClose={handleNavCancel}
        header={{
          type: 'warning',
          title: i18n.t('workflows.nav_confirm_modal.title'),
          description: i18n.t('workflows.nav_confirm_modal.description'),
        }}
        actions={{
          primary: {
            label: i18n.t('workflows.nav_confirm_modal.continue_editing'),
            onClick: handleNavContinue,
          },
          secondary: {
            label: i18n.t('workflows.nav_confirm_modal.discard'),
            onClick: handleNavDiscard,
          },
        }}
      />

      {/* Modals */}
      {activeModal === 'publish' && (
        <PublishTypeModal
          isTypePublished={isTypePublished}
          onClose={() => setActiveModal(null)}
          onPublish={handlePublish}
          onPublishLoading={isPublishing || isPublishingChanges}
          publishingForm={!isLastFormVersionPublished}
          publishingWorkflow={!isLastWorkflowVersionPublished}
        />
      )}
      {activeModal === 'save' && (
        <SaveRequestTypeModal
          onClose={() => setActiveModal(null)}
          onSave={handleSave}
          onSaveLoading={isSaving}
          savingForm={hasFormChanges || hasRestoredContent}
          savingWorkflow={hasWorkflowChanges || hasRestoredContent}
        />
      )}
      {isVersionHistoryOpen && id && (
        <VersionHistory
          procurementTypeId={id ?? ''}
          companyId={company.id}
          currentEditingVersionId={currentEditingVersionId}
          onClose={() => setIsVersionHistoryOpen(false)}
          onRestore={handleVersionRestore}
        />
      )}
      {/* Header */}
      {requestType && (
        <RequestTypeDetailHeader
          draftVersionInfo={draftVersionInfo}
          hasDraftChanges={hasDraftChanges}
          hasLocalChanges={hasLocalChanges}
          isPublished={isTypePublished}
          onPublish={() => setActiveModal('publish')}
          onSave={() => setActiveModal('save')}
          onVersionHistory={() => setIsVersionHistoryOpen(true)}
          publishedVersionNumber={publishedVersionNumber}
          title={requestType.name}
        />
      )}

      {/* Tab content */}
      <TabContent
        isCompleteFormEnabled={isCompleteFormEnabled}
        isVendorOnboardingEnabled={isVendorOnboardingEnabled}
        id={id}
        requestType={requestType}
        workflow={workflow}
        formModel={formModel}
        formState={formState}
        setFormState={setFormState}
        isFormLoading={loading || !versionsReady || effectiveFormLoading}
        coCreationKey={restoredVersion?.versionId ?? id}
      />
    </ResponsivePage.Body>
  )
}

// Container component that provides WorkflowProvider context
const RequestTypeDetailPage = () => {
  return (
    <WorkflowProvider>
      <RequestTypeDetailPageContent />
    </WorkflowProvider>
  )
}

export default RequestTypeDetailPage
